import { useMutation, useQuery } from "@apollo/client";
import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import {
  Navigate,
  Route,
  Routes as RouterRoutes,
  useParams,
} from "react-router-dom";
import * as Yup from "yup";

import AuthRoute from "../../../../components/AuthRoute";
import { NavBar, Spinner } from "../../../../components/ui";
import { UPDATE_WAREHOUSE_ITEM as updateWarehouseItemMutation } from "../../../../graphql/mutation/WarehouseItem";
import { FETCH_WAREHOUSE_ITEM as fetchWarehouseItem } from "../../../../graphql/query/WarehouseItem";
import { Portal, getChangedFields } from "../../../../util";
import { parseWarehouseItemId } from "../../warehouseItemNavigation";
import routes from "./routes";

const getWarehouseItemInitialValues = warehouseItem => warehouseItem ?? {};

const Body = props => {
  const { loading, ...remainingProps } = props;

  return loading ? (
    <div className="h-100% flex w-full items-center justify-center">
      <Spinner />
    </div>
  ) : (
    <RouterRoutes>
      {routes
        ?.filter?.(item => item?.element)
        ?.map((item, index) => {
          return (
            <Route
              key={index}
              path={item?.href}
              element={
                <AuthRoute permissions={item.permissions}>
                  <item.element {...remainingProps} />
                </AuthRoute>
              }
            />
          );
        })}
    </RouterRoutes>
  );
};

const Wizard = props => {
  const { id } = useParams();
  const warehouseItemId = parseWarehouseItemId(id);
  const abortController = useRef();
  const [calculatedDimension, setCalculatedDimension] = useState({
    depthIn: 0,
    heightIn: 0,
    widthIn: 0,
    area: 0,
    volume: 0,
  });

  const { data: { warehouseItem } = {}, loading } = useQuery(
    fetchWarehouseItem(
      {
        // Core fields needed by Detail and Information components
        artistId: true,
        categoryId: true,
        container: true,
        description: true,
        destroyed: true,
        dimd: true,
        dimh: true,
        dimw: true,
        externalRef: true,
        image: true,
        instanceId: true,
        itemDescription: true,
        link: true,
        mgroup: true,
        notes: true,
        packingId: true,
        responsibleGroupId: true,
        weight: true,
        weightTypeId: true,
        // Nested objects
        itemSite: {
          location: true,
        },
        instance: { artworkId: true, isBond: true },
      },
      { fetchPolicy: "network-only" },
    ),
    {
      skip: warehouseItemId == null,
      variables: { id: warehouseItemId },
    },
  );

  const [
    updateWarehouseItem,
    {
      data: { updateWarehouseItem: { error: updateError } = {} } = {},
      loading: updateLoading,
    },
  ] = useMutation(updateWarehouseItemMutation());

  const [itemIsBonded, setItemIsBonded] = useState(false);
  const [bondedWarningCount, setBondedWarningCount] = useState(0);

  const formik = useFormik({
    initialValues: getWarehouseItemInitialValues(warehouseItem),
    enableReinitialize: true,
    validateOnChange: true,
    validateOnMount: true,
    validationSchema: Yup.object({
      dimd: Yup.number().positive().nullable(),
      dimh: Yup.number().positive().nullable(),
      dimw: Yup.number().positive().nullable(),
      weight: Yup.number().positive().nullable(),
    }),
    onSubmit: values => {
      const changedFields = getChangedFields(values, warehouseItem);
      if (Object.keys(changedFields).length > 0) {
        const controller = new AbortController();
        abortController.current = controller;
        updateWarehouseItem({
          variables: {
            input: {
              id: warehouseItemId,
              ...changedFields,
            },
          },
          context: {
            fetchOptions: {
              signal: controller.signal,
            },
          },
        });
      }
    },
  });

  const fineArtInstanceIsBonded = (categoryId, instance) => {
    if (!categoryId || !instance) setItemIsBonded(false);
    if (categoryId === 1 && instance?.isBond) {
      setItemIsBonded(true);
    }
  };

  const calculateDimensions = values => {
    const { dimd, dimh, dimw } = values;

    setCalculatedDimension({
      depthIn: (dimd / 2.54).toFixed(2),
      heightIn: (dimh / 2.54).toFixed(2),
      widthIn: (dimw / 2.54).toFixed(2),
      area: (((dimd / 100) * dimw) / 100).toFixed(2),
      volume: (((((dimd / 100) * dimw) / 100) * dimh) / 100).toFixed(2),
    });
  };

  useEffect(() => {
    warehouseItem && calculateDimensions(warehouseItem);
    warehouseItem &&
      fineArtInstanceIsBonded(
        warehouseItem?.categoryId,
        warehouseItem?.instance,
      );
  }, [warehouseItem]);

  useEffect(() => {
    if (formik?.values && abortController.current) {
      abortController.current.abort();
    }
  }, [formik?.values]);

  const bodyProps = {
    ...props,
    calculatedDimension,
    formik,
    loading,
    updateError,
    updateLoading,
    updateWarehouseItem,
    warehouseItem,
    warehouseItemId,
    showBondedWarning: itemIsBonded && bondedWarningCount === 0,
    setItemIsBonded,
    setBondedWarningCount,
  };

  if (warehouseItemId == null) {
    return <Navigate replace to="/warehouse?status=all" />;
  }

  return (
    <div className="relative flex min-h-full">
      <Portal id="sidebar">
        <NavBar
          navigation={routes?.filter?.(item => item?.icon)}
          open={props?.open}
        />
      </Portal>
      <div className="flex min-w-0 flex-1 overflow-x-auto px-4 py-4 sm:px-6 lg:px-8 lg:py-8">
        <Body {...bodyProps} />
      </div>
    </div>
  );
};

export { getWarehouseItemInitialValues };
export default Wizard;
